
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {
    PermissionsGroup,
} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import { roundToFixed } from '@/util/number';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import TableCollapseToggle from '@/components/TableCollapseToggle.vue';
import moment from 'moment';
import { getTableName, TIMEZONE_OFFSET_MS } from '@/worker/db/sqlHelpers';

@Component({
    components: {
        TableFilterable,
        TableCollapseToggle,
    },
})
export default class BpCostOfGoodsSoldTable extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['transactions'];
    }

    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'projects'
    > = ['dateRange'];

    public tableItems: any[] = [];
    public tableFields: any[] = [];
    public periodTotalData: any[] = [];
    public periodTotalFields: any[] = [];

    public get exportFileName() {
        const formattedDate = [
            moment(this.selectedDateRange[0]).format('DD MMM YY'),
            moment(this.selectedDateRange[1]).format('DD MMM YY'),
        ];
        return (
            'Cost of Goods Sold' + '_'
            + formattedDate[0] + ' to ' + formattedDate[1]
        );
    }

    public get dateFormatted() {
        return this.selectedDateRange;
    }

    public get currencySymbol() {
        return gdbx.currencySymbol;
    }

    public get numDecimal() {
        return gdbx.numDecimal;
    }

    public get expensiveHook() {
        const {
          selectedDateRange,
          selectedProjects,
        } = this;
        return JSON.stringify([
          selectedDateRange,
          selectedProjects,
        ]);
    }

    public openingStockDateRange(
        dateRange: [number, number],
    ): [number, number] {
        return [
            moment(dateRange[0]).add(-1, 'month').valueOf(),
            moment(dateRange[1]).add(-1, 'month').valueOf(),
        ];
    }

    public async expensiveCalc() {
        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        const table = getTableName('transactions');
        const stockTable = getTableName('stockBalances');
        const range = this.selectedDateRange;
        const openRange = this.openingStockDateRange(
            this.selectedDateRange as [number, number],
        );
        const amt = 'TRY_CAST("AMOUNT" AS DOUBLE)';
        const dateExpr = 'TRY_CAST("DOCDATE" AS BIGINT)';
        const yearExpr = `EXTRACT(YEAR FROM EPOCH_MS(`
            + `${dateExpr} + ${TIMEZONE_OFFSET_MS}))`;
        const monthExpr = `EXTRACT(MONTH FROM EPOCH_MS(`
            + `${dateExpr} + ${TIMEZONE_OFFSET_MS}))`;

        let projectFilter = '';
        if (this.selectedProjects.length > 0) {
            const list = this.selectedProjects
                .map((p) => `'${escapeStr(p)}'`).join(',');
            projectFilter = ` AND "PROJECT" IN (${list})`;
        }

        const jobId = this.currentJobId;
        const queryHead = await this._ensureTablesLoaded(
            ['transactions', 'stockBalances'],
        );
        this.checkInterrupted(jobId);

        this.generateLoadingText(0.3);

        const [coResult, mcResult, openStockResult, closeStockResult]
            = await Promise.all([
                // CO (non-MC) cross-tab
                queryHead.rawQuery(`
                    SELECT "SPECIALACCTYPECODE" as code,
                        "SPECIALACCTYPENAME" as name,
                        ${yearExpr} as year,
                        ${monthExpr} as month,
                        SUM(${amt}) as total
                    FROM ${table}
                    WHERE ${dateExpr}
                        BETWEEN ${range[0]} AND ${range[1]}
                        AND "ACCTYPE" = 'CO'
                        AND "SPECIALACCTYPE" != 'MC'
                        ${projectFilter}
                    GROUP BY code, name, year, month
                `),
                // MC cross-tab
                queryHead.rawQuery(`
                    SELECT "SPECIALACCTYPECODE" as code,
                        "SPECIALACCTYPENAME" as name,
                        ${yearExpr} as year,
                        ${monthExpr} as month,
                        SUM(${amt}) as total
                    FROM ${table}
                    WHERE ${dateExpr}
                        BETWEEN ${range[0]} AND ${range[1]}
                        AND "ACCTYPE" = 'CO'
                        AND "SPECIALACCTYPE" = 'MC'
                        ${projectFilter}
                    GROUP BY code, name, year, month
                `),
                // Opening stock by month
                queryHead.rawQuery(`
                    SELECT "SYEAR" as year,
                        "SMONTH" as month,
                        SUM(${amt}) as total
                    FROM ${stockTable}
                    WHERE ${dateExpr}
                        BETWEEN ${openRange[0]}
                        AND ${openRange[1]}
                        ${projectFilter}
                    GROUP BY "SYEAR", "SMONTH"
                `),
                // Closing stock by month
                queryHead.rawQuery(`
                    SELECT "SYEAR" as year,
                        "SMONTH" as month,
                        SUM(${amt}) as total
                    FROM ${stockTable}
                    WHERE ${dateExpr}
                        BETWEEN ${range[0]} AND ${range[1]}
                        ${projectFilter}
                    GROUP BY "SYEAR", "SMONTH"
                `),
            ]);
        this.checkInterrupted(jobId);

        // Build month labels from main date range
        const startMoment = moment(range[0]).startOf('month');
        const endMoment = moment(range[1]).endOf('month');
        const months: Array<{
            label: string; year: number;
            month: number;
        }> = [];
        const mm = startMoment.clone();
        while (mm.isSameOrBefore(endMoment, 'month')) {
            months.push({
                label: mm.format('MMM YYYY'),
                year: mm.year(),
                month: mm.month() + 1,
            });
            mm.add(1, 'month');
        }

        // Build opening stock month labels (shifted back 1 month)
        const openStartMoment =
            moment(openRange[0]).startOf('month');
        const openEndMoment =
            moment(openRange[1]).endOf('month');
        const openMonths: Array<{
            label: string; year: number;
            month: number;
        }> = [];
        const omm = openStartMoment.clone();
        while (omm.isSameOrBefore(openEndMoment, 'month')) {
            openMonths.push({
                label: omm.format('MMM YYYY'),
                year: omm.year(),
                month: omm.month() + 1,
            });
            omm.add(1, 'month');
        }

        // Build CO lookup
        const coCodeNameMap = new Map<string, string>();
        const coCrossTab: Record<string, number> = {};
        for (const row of coResult) {
            const code = String(row.code);
            coCodeNameMap.set(code, String(row.name));
            const key = `${code}-${Number(row.year)}`
                + `-${Number(row.month)}`;
            coCrossTab[key] = Number(row.total) || 0;
        }
        const coCodes = [...coCodeNameMap.entries()];

        // Build MC lookup
        const mcCodeNameMap = new Map<string, string>();
        const mcCrossTab: Record<string, number> = {};
        for (const row of mcResult) {
            const code = String(row.code);
            mcCodeNameMap.set(code, String(row.name));
            const key = `${code}-${Number(row.year)}`
                + `-${Number(row.month)}`;
            mcCrossTab[key] = Number(row.total) || 0;
        }
        const mcCodes = [...mcCodeNameMap.entries()];

        // Build stock lookups (year-month -> total)
        const openStockMap: Record<string, number> = {};
        for (const row of openStockResult) {
            const key = `${Number(row.year)}-${Number(row.month)}`;
            openStockMap[key] = Number(row.total) || 0;
        }
        const closeStockMap: Record<string, number> = {};
        for (const row of closeStockResult) {
            const key = `${Number(row.year)}-${Number(row.month)}`;
            closeStockMap[key] = Number(row.total) || 0;
        }

        // Build table fields
        this.tableFields = [
            {
                key: 'type', sortable: true,
                stickyColumn: true,
            },
            {
                key: 'particular', sortable: true,
                stickyColumn: true,
            },
            {
                key: 'total',
                sortable: true,
                formatter: TableItemFormatter.currencySplit,
            },
            ...months.map((m) => ({
                key: m.label,
                sortable: true,
                formatter: TableItemFormatter.currencySplit,
            })),
        ];

        this.periodTotalFields = [
            {
                key: 'type', sortable: true,
                textAlign: 'center',
            },
            {
                key: 'total', sortable: true,
                formatter: TableItemFormatter.currencySplit,
                textAlign: 'center',
            },
        ];

        // Top-5 value arrays
        const coMonthVals: number[][] = months.map((m) =>
            coCodes.map(([code]) =>
                coCrossTab[
                    `${code}-${m.year}-${m.month}`
                ] || 0,
            ),
        );
        const mcMonthVals: number[][] = months.map((m) =>
            mcCodes.map(([code]) =>
                mcCrossTab[
                    `${code}-${m.year}-${m.month}`
                ] || 0,
            ),
        );

        // Build CO table items
        const coTable = coCodes.map(([code, name]) => {
            const item: any = {
                type: 'COGS',
                particular: name,
                total: 0,
                _cellVariants: {},
            };
            months.forEach((m, mIdx) => {
                const val = coCrossTab[
                    `${code}-${m.year}-${m.month}`
                ] || 0;
                item[m.label] = val;
                item.total += val;
                const sorted = coMonthVals[mIdx]
                    .slice().sort((a, b) => b - a)
                    .filter((v) => v !== 0)
                    .slice(0, 5);
                item._cellVariants[m.label] =
                    sorted.includes(val) ? 'top-five' : '';
            });
            return item;
        });

        // Build MC table items
        const mcTable = mcCodes.map(([code, name]) => {
            const item: any = {
                type: 'MFGC',
                particular: name,
                total: 0,
                _cellVariants: {},
            };
            months.forEach((m, mIdx) => {
                const val = mcCrossTab[
                    `${code}-${m.year}-${m.month}`
                ] || 0;
                item[m.label] = val;
                item.total += val;
                const sorted = mcMonthVals[mIdx]
                    .slice().sort((a, b) => b - a)
                    .filter((v) => v !== 0)
                    .slice(0, 5);
                item._cellVariants[m.label] =
                    sorted.includes(val) ? 'top-five' : '';
            });
            return item;
        });

        // Build stock rows
        let openingStocksTotal = 0;
        let closingStocksTotal = 0;

        const openingStockRow: any = {
            type: 'STOCK',
            particular: 'Opening Stock',
            total: 0,
            _cellVariants: {},
        };
        const closingStockRow: any = {
            type: 'STOCK',
            particular: 'Closing Stock',
            total: 0,
            _cellVariants: {},
        };

        // Opening stock uses shifted month labels
        openMonths.forEach((om) => {
            const val = openStockMap[
                `${om.year}-${om.month}`
            ] || 0;
            openingStockRow[om.label] = val;
            openingStocksTotal += val;
        });
        openingStockRow.total = openingStocksTotal;

        // Closing stock uses main month labels
        months.forEach((m) => {
            const val = closeStockMap[
                `${m.year}-${m.month}`
            ] || 0;
            closingStockRow[m.label] = val;
            closingStocksTotal += val;
        });
        closingStockRow.total = closingStocksTotal;

        this.tableItems = [
            openingStockRow,
            ...coTable,
            ...mcTable,
            closingStockRow,
        ];

        // Period total data
        const coTotal = coTable.reduce(
            (a, b) => a + b.total, 0,
        );
        const mcTotal = mcTable.reduce(
            (a, b) => a + b.total, 0,
        );

        this.periodTotalData = [
            { type: 'Opening Stock', total: openingStocksTotal },
            { type: 'COGS', total: coTotal },
            { type: 'Manufacturing Costs', total: mcTotal },
            { type: 'Closing Stock', total: closingStocksTotal },
        ];

        this.generateLoadingText(1);

        this.saveHistory(
            'tableItems',
            'tableFields',
            'periodTotalData',
            'periodTotalFields',
        );
    }
}
